import { AppThunk } from "appThunk";
import { GeoIndicator, Store } from "modules/customer/insights/portfolioNew/store";
import { ExtendedResultSet, cubeLoadCatchmentArea } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

export enum DemographicIndicator {
    HouseholdIncome = "householdIncome",
    HouseholdOccupancy = "householdOccupancy",
    HouseholdTenure = "householdTenure",
    AgeStructureGB = "ageStructureGB",
    AgeStructureNI = "ageStructureNI",
    ChildrenAgeStructure = "childrenAgeStructure",
    CountryOfBirth = "countryOfBirth",
    Ethnicity = "ethnicity",
    FirstLanguage = "firstLanguage",
    DwellingType = "dwellingType",
    HouseholdNumberOfCars = "householdNumberOfCars",
    PopulationDensity = "populationDensity",
    EducationLevel = "educationLevel"
}

export enum DemographicIndicatorMeasure {
    PercentageOfPopulation,
    PopulationInThousands
}

export class DwellingType {
    public readonly detached: number;
    public readonly semiDetached: number;
    public readonly terrace: number;
    public readonly flat: number;
    public readonly otherAccommodation: number;

    constructor(
        detached: number,
        semiDetached: number,
        terrace: number,
        flat: number,
        otherAccommodation: number
    ) {
        this.detached = detached;
        this.semiDetached = semiDetached;
        this.terrace = terrace;
        this.flat = flat;
        this.otherAccommodation = otherAccommodation;
    }
}

export class HouseholdTenure {
    public readonly ownedOutright: number;
    public readonly ownedWithLoan: number;
    public readonly socialRent: number;
    public readonly privateRent: number;
    public readonly sharedOwnership: number;
    public readonly livingRentFree: number;

    constructor(
        ownedOutright: number,
        ownedWithLoan: number,
        socialRent: number,
        privateRent: number,
        sharedOwnership: number,
        livingRentFree: number
    ) {
        this.ownedOutright = ownedOutright;
        this.ownedWithLoan = ownedWithLoan;
        this.socialRent = socialRent;
        this.privateRent = privateRent;
        this.sharedOwnership = sharedOwnership;
        this.livingRentFree = livingRentFree;
    }
}

export class ChildrenAgeStructure {
    public readonly childrenAgeLessThan1: number | null;
    public readonly childrenAge1to2: number | null;
    public readonly childrenAge3to5: number | null;
    public readonly childrenAge6to10: number | null;
    public readonly childrenAge11to16: number | null;

    constructor(
        childrenAgeLessThan1: number | null,
        childrenAge1to2: number | null,
        childrenAge3to5: number | null,
        childrenAge6to10: number | null,
        childrenAge11to16: number | null
    ) {
        this.childrenAgeLessThan1 = childrenAgeLessThan1;
        this.childrenAge1to2 = childrenAge1to2;
        this.childrenAge3to5 = childrenAge3to5;
        this.childrenAge6to10 = childrenAge6to10;
        this.childrenAge11to16 = childrenAge11to16;
    }
}

export class HouseholdOccupancy {
    public readonly occupancyRating2Plus: number | null;
    public readonly occupancyRating1: number | null;
    public readonly occupancyRating0: number | null;
    public readonly occupancyRatingMinus1: number | null;
    public readonly occupancyRatingMinus2orLess: number | null;

    constructor(
        occupancyRating2Plus: number | null,
        occupancyRating1: number | null,
        occupancyRating0: number | null,
        occupancyRatingMinus1: number | null,
        occupancyRatingMinus2orLess: number | null
    ) {
        this.occupancyRating2Plus = occupancyRating2Plus;
        this.occupancyRating1 = occupancyRating1;
        this.occupancyRating0 = occupancyRating0;
        this.occupancyRatingMinus1 = occupancyRatingMinus1;
        this.occupancyRatingMinus2orLess = occupancyRatingMinus2orLess;
    }
}

export class AgeStructureGB {
    public readonly age0to16: number | null;
    public readonly age17to25: number | null;
    public readonly age26to35: number | null;
    public readonly age36to45: number | null;
    public readonly age46to55: number | null;
    public readonly age56to65: number | null;
    public readonly age66Plus: number | null;

    constructor(
        age0to16: number | null,
        age17to25: number | null,
        age26to35: number | null,
        age36to45: number | null,
        age46to55: number | null,
        age56to65: number | null,
        age66Plus: number | null
    ) {
        this.age0to16 = age0to16;
        this.age17to25 = age17to25;
        this.age26to35 = age26to35;
        this.age36to45 = age36to45;
        this.age46to55 = age46to55;
        this.age56to65 = age56to65;
        this.age66Plus = age66Plus;
    }
}

export class AgeStructureNI {
    public readonly age0to15: number | null;
    public readonly age16to24: number | null;
    public readonly age25to44: number | null;
    public readonly age45to64: number | null;
    public readonly age65Plus: number | null;

    constructor(
        age0to15: number | null,
        age16to24: number | null,
        age25to44: number | null,
        age45to64: number | null,
        age65Plus: number | null
    ) {
        this.age0to15 = age0to15;
        this.age16to24 = age16to24;
        this.age25to44 = age25to44;
        this.age45to64 = age45to64;
        this.age65Plus = age65Plus;
    }
}

export class CountryOfBirth {
    public readonly bornInUK: number;
    public readonly emigratedToUK: number;

    constructor(bornInUK: number, emigratedToUK: number) {
        this.bornInUK = bornInUK;
        this.emigratedToUK = emigratedToUK;
    }
}

export class Ethnicity {
    public readonly arab: number;
    public readonly asianOther: number;
    public readonly bangladeshi: number;
    public readonly blackAfrican: number;
    public readonly blackCaribbean: number;
    public readonly blackOther: number;
    public readonly chinese: number;
    public readonly indian: number;
    public readonly mixed: number;
    public readonly other: number;
    public readonly pakistani: number;
    public readonly white: number;

    constructor(
        arab: number,
        asianOther: number,
        bangladeshi: number,
        blackAfrican: number,
        blackCaribbean: number,
        blackOther: number,
        chinese: number,
        indian: number,
        mixed: number,
        other: number,
        pakistani: number,
        white: number
    ) {
        this.arab = arab;
        this.asianOther = asianOther;
        this.bangladeshi = bangladeshi;
        this.blackAfrican = blackAfrican;
        this.blackCaribbean = blackCaribbean;
        this.blackOther = blackOther;
        this.chinese = chinese;
        this.indian = indian;
        this.mixed = mixed;
        this.other = other;
        this.pakistani = pakistani;
        this.white = white;
    }
}

export class FirstLanguage {
    public readonly englishOrWelsh: number;
    public readonly other: number;

    constructor(englishOrWelsh: number, other: number) {
        this.englishOrWelsh = englishOrWelsh;
        this.other = other;
    }
}

export class HouseholdNumberOfCars {
    public readonly car3Plus: number;
    public readonly car2: number;
    public readonly car1: number;
    public readonly car0: number;

    constructor(
        car3Plus: number,
        car2: number,
        car1: number,
        car0: number
    ) {
        this.car3Plus = car3Plus;
        this.car2 = car2;
        this.car1 = car1;
        this.car0 = car0;
    }
}

export class PopulationDensity {
    public readonly popDensityLess2000: number;
    public readonly popDensity2000to3999: number;
    public readonly popDensity4000to5999: number;
    public readonly popDensity6000to10000: number;
    public readonly popDensity10000Plus: number;

    constructor(
        popDensityLess2000: number,
        popDensity2000to3999: number,
        popDensity4000to5999: number,
        popDensity6000to10000: number,
        popDensity10000Plus: number
    ) {
        this.popDensityLess2000 = popDensityLess2000;
        this.popDensity2000to3999 = popDensity2000to3999;
        this.popDensity4000to5999 = popDensity4000to5999;
        this.popDensity6000to10000 = popDensity6000to10000;
        this.popDensity10000Plus = popDensity10000Plus;
    }
}

export class HouseholdIncome {
    public readonly countIncome100kPlus: number | null;
    public readonly countIncome90to100k: number | null;
    public readonly countIncome80to90k: number | null;
    public readonly countIncome70to80k: number | null;
    public readonly countIncome60to70k: number | null;
    public readonly countIncome50to60k: number | null;
    public readonly countIncome40to50k: number | null;
    public readonly countIncome30to40k: number | null;
    public readonly countIncome20to30k: number | null;
    public readonly countIncomeSub20k: number | null;

    constructor(
        countIncome100kPlus: number | null,
        countIncome90to100k: number | null,
        countIncome80to90k: number | null,
        countIncome70to80k: number | null,
        countIncome60to70k: number | null,
        countIncome50to60k: number | null,
        countIncome40to50k: number | null,
        countIncome30to40k: number | null,
        countIncome20to30k: number | null,
        countIncomeSub20k: number | null
    ) {
        this.countIncome100kPlus = countIncome100kPlus;
        this.countIncome90to100k = countIncome90to100k;
        this.countIncome80to90k = countIncome80to90k;
        this.countIncome70to80k = countIncome70to80k;
        this.countIncome60to70k = countIncome60to70k;
        this.countIncome50to60k = countIncome50to60k;
        this.countIncome40to50k = countIncome40to50k;
        this.countIncome30to40k = countIncome30to40k;
        this.countIncome20to30k = countIncome20to30k;
        this.countIncomeSub20k = countIncomeSub20k;
    }
}

export class EducationLevel {
    public readonly noQualification: number | null;
    public readonly level1: number | null;
    public readonly level2: number | null;
    public readonly level3: number | null;
    public readonly level4Plus: number | null;
    public readonly otherQualification: number | null;

    constructor(
        noQualification: number | null,
        level1: number | null,
        level2: number | null,
        level3: number | null,
        level4Plus: number | null,
        otherQualification: number | null
    ) {
        this.noQualification = noQualification;
        this.level1 = level1;
        this.level2 = level2;
        this.level3 = level3;
        this.level4Plus = level4Plus;
        this.otherQualification = otherQualification;
    }
}

export class CatchmentDemographics {
    public readonly storeID: string;
    public readonly dwellingType: DwellingType;
    public readonly householdTenure: HouseholdTenure;
    public readonly childrenAgeStructure: ChildrenAgeStructure;
    public readonly householdOccupancy: HouseholdOccupancy;
    public readonly ageStructureGB: AgeStructureGB;
    public readonly ageStructureNI: AgeStructureNI;
    public readonly countryOfBirth: CountryOfBirth;
    public readonly ethnicity: Ethnicity;
    public readonly firstLanguage: FirstLanguage;
    public readonly householdNumberOfCars: HouseholdNumberOfCars;
    public readonly populationDensity: PopulationDensity;
    public readonly householdIncome: HouseholdIncome;
    public readonly educationLevel: EducationLevel;

    constructor(
        storeID: string,
        dwellingType: DwellingType,
        householdTenure: HouseholdTenure,
        childrenAgeStructure: ChildrenAgeStructure,
        householdOccupancy: HouseholdOccupancy,
        ageStructureGB: AgeStructureGB,
        ageStructureNI: AgeStructureNI,
        countryOfBirth: CountryOfBirth,
        ethnicity: Ethnicity,
        firstLanguage: FirstLanguage,
        householdNumberOfCars: HouseholdNumberOfCars,
        populationDensity: PopulationDensity,
        householdIncome: HouseholdIncome,
        educationLevel: EducationLevel
    ) {
        this.storeID = storeID;
        this.householdIncome = householdIncome;
        this.householdOccupancy = householdOccupancy;
        this.householdTenure = householdTenure;
        this.ageStructureGB = ageStructureGB;
        this.ageStructureNI = ageStructureNI;
        this.childrenAgeStructure = childrenAgeStructure;
        this.countryOfBirth = countryOfBirth;
        this.ethnicity = ethnicity;
        this.firstLanguage = firstLanguage;
        this.dwellingType = dwellingType;
        this.householdNumberOfCars = householdNumberOfCars;
        this.populationDensity = populationDensity;
        this.educationLevel = educationLevel;
    }
}

export const loadCatchmentDemographics = (selectedAndComparatorStores: Store[], catchmentAccountId: string): AppThunk<Promise<CatchmentDemographics[]>> => async (dispatch) => {

    try {

        const retailCentreIDsByStoreCategoryID = new Map<number, number[]>();
        selectedAndComparatorStores.forEach(store => {
            const retailCentreIDs = retailCentreIDsByStoreCategoryID.get(store.storeCategoryID) ?? [];
            retailCentreIDs.push(store.retailCentreID);
            retailCentreIDsByStoreCategoryID.set(store.storeCategoryID, retailCentreIDs);
        });

        const orFilterClause = { or: [] };
        retailCentreIDsByStoreCategoryID.forEach((retailCentreIDs, storeCategoryID) => {
            const andFilterClause = {
                and: [{
                    member: "CatchmentDemographics.StoreCategory_ID",
                    operator: "equals",
                    values: [String(storeCategoryID)]
                }, {
                    member: "CatchmentDemographics.RetailCentreID",
                    operator: "equals",
                    values: retailCentreIDs.map(String)
                }]
            };
            // @ts-ignore
            orFilterClause.or.push(andFilterClause);
        });

        const query = {
            dimensions: [
                "CatchmentDemographics.RetailCentreID",
                "CatchmentDemographics.StoreCategory_ID",
                "CatchmentDemographics.Detached",
                "CatchmentDemographics.SemiDetached",
                "CatchmentDemographics.Terrace",
                "CatchmentDemographics.Flat",
                "CatchmentDemographics.OtherAccommodation",
                "CatchmentDemographics.OwnedOutright",
                "CatchmentDemographics.OwnedWithLoan",
                "CatchmentDemographics.SocialRent",
                "CatchmentDemographics.PrivateRent",
                "CatchmentDemographics.SharedOwnership",
                "CatchmentDemographics.LivingRentFree",
                "CatchmentDemographics.ChildrenAgeLessThan1",
                "CatchmentDemographics.ChildrenAge1to2",
                "CatchmentDemographics.ChildrenAge3to5",
                "CatchmentDemographics.ChildrenAge6to10",
                "CatchmentDemographics.ChildrenAge11to16",
                "CatchmentDemographics.OccupancyRating_2Plus",
                "CatchmentDemographics.OccupancyRating_1",
                "CatchmentDemographics.OccupancyRating_0",
                "CatchmentDemographics.OccupancyRating_Minus1",
                "CatchmentDemographics.OccupancyRating_Minus2orLess",
                "CatchmentDemographics.Age0to16",
                "CatchmentDemographics.Age17to25",
                "CatchmentDemographics.Age26to35",
                "CatchmentDemographics.Age36to45",
                "CatchmentDemographics.Age46to55",
                "CatchmentDemographics.Age56to65",
                "CatchmentDemographics.Age66Plus",
                "CatchmentDemographics.Age0to15",
                "CatchmentDemographics.Age16to24",
                "CatchmentDemographics.Age25to44",
                "CatchmentDemographics.Age45to64",
                "CatchmentDemographics.Age65Plus",
                "CatchmentDemographics.NoQualification",
                "CatchmentDemographics.Level1",
                "CatchmentDemographics.Level2",
                "CatchmentDemographics.Level3",
                "CatchmentDemographics.Level4Plus",
                "CatchmentDemographics.OtherQualification",
                "CatchmentDemographics.BornInUk",
                "CatchmentDemographics.EmigratedToUk",
                "CatchmentDemographics.EthArab",
                "CatchmentDemographics.EthAsianOther",
                "CatchmentDemographics.EthBangladeshi",
                "CatchmentDemographics.EthBlackAfrican",
                "CatchmentDemographics.EthBlackCaribbean",
                "CatchmentDemographics.EthBlackOther",
                "CatchmentDemographics.EthChinese",
                "CatchmentDemographics.EthIndian",
                "CatchmentDemographics.EthMixed",
                "CatchmentDemographics.EthOther",
                "CatchmentDemographics.EthPakistani",
                "CatchmentDemographics.EthWhite",
                "CatchmentDemographics.LangEnglishOrWelsh",
                "CatchmentDemographics.LangOther",
                "CatchmentDemographics.Car3Plus",
                "CatchmentDemographics.Car2",
                "CatchmentDemographics.Car1",
                "CatchmentDemographics.Car0",
                "CatchmentDemographics.PopDensityLess2000",
                "CatchmentDemographics.PopDensity2000to3999",
                "CatchmentDemographics.PopDensity4000to5999",
                "CatchmentDemographics.PopDensity6000to10000",
                "CatchmentDemographics.PopDensity10000Plus",
                "CatchmentDemographics.CountIncome_100kPlus",
                "CatchmentDemographics.CountIncome_90to100k",
                "CatchmentDemographics.CountIncome_80to90k",
                "CatchmentDemographics.CountIncome_70to80k",
                "CatchmentDemographics.CountIncome_60to70k",
                "CatchmentDemographics.CountIncome_50to60k",
                "CatchmentDemographics.CountIncome_40to50k",
                "CatchmentDemographics.CountIncome_30to40k",
                "CatchmentDemographics.CountIncome_20to30k",
                "CatchmentDemographics.CountIncome_Sub20k"
            ],
            segments: [
                "CatchmentDemographics.Baseline"
            ],
            filters: [orFilterClause]
        };

        const results = await dispatch(cubeLoadCatchmentArea(query, catchmentAccountId, "CatchmentDemographics")) as unknown as ExtendedResultSet<any>;
        const rawData = results.loadResponses[0].data;
        const catchmentDemographics: CatchmentDemographics[] = [];
        selectedAndComparatorStores.forEach(store => {
            const isNIStore = store.geoIndicator === GeoIndicator.NI;
            const relevantResult = rawData.find(relevantResult =>
                Number(relevantResult["CatchmentDemographics.RetailCentreID"]) === store.retailCentreID
                && Number(relevantResult["CatchmentDemographics.StoreCategory_ID"]) === store.storeCategoryID);
            if (relevantResult) {
                const catchmentDemographic = new CatchmentDemographics(
                    store.id,
                    new DwellingType(
                        Number(relevantResult["CatchmentDemographics.Detached"]),
                        Number(relevantResult["CatchmentDemographics.SemiDetached"]),
                        Number(relevantResult["CatchmentDemographics.Terrace"]),
                        Number(relevantResult["CatchmentDemographics.Flat"]),
                        Number(relevantResult["CatchmentDemographics.OtherAccommodation"])
                    ),
                    new HouseholdTenure(
                        Number(relevantResult["CatchmentDemographics.OwnedOutright"]),
                        Number(relevantResult["CatchmentDemographics.OwnedWithLoan"]),
                        Number(relevantResult["CatchmentDemographics.SocialRent"]),
                        Number(relevantResult["CatchmentDemographics.PrivateRent"]),
                        Number(relevantResult["CatchmentDemographics.SharedOwnership"]),
                        Number(relevantResult["CatchmentDemographics.LivingRentFree"])
                    ),
                    new ChildrenAgeStructure(
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.ChildrenAgeLessThan1"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.ChildrenAge1to2"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.ChildrenAge3to5"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.ChildrenAge6to10"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.ChildrenAge11to16"])
                    ),
                    new HouseholdOccupancy(
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.OccupancyRating_2Plus"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.OccupancyRating_1"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.OccupancyRating_0"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.OccupancyRating_Minus1"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.OccupancyRating_Minus2orLess"])
                    ),
                    new AgeStructureGB(
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Age0to16"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Age17to25"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Age26to35"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Age36to45"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Age46to55"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Age56to65"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Age66Plus"])
                    ),
                    new AgeStructureNI(
                        isNIStore ? Number(relevantResult["CatchmentDemographics.Age0to15"]) : null,
                        isNIStore ? Number(relevantResult["CatchmentDemographics.Age16to24"]) : null,
                        isNIStore ? Number(relevantResult["CatchmentDemographics.Age25to44"]) : null,
                        isNIStore ? Number(relevantResult["CatchmentDemographics.Age45to64"]) : null,
                        isNIStore ? Number(relevantResult["CatchmentDemographics.Age65Plus"]) : null
                    ),
                    new CountryOfBirth(
                        Number(relevantResult["CatchmentDemographics.BornInUk"]),
                        Number(relevantResult["CatchmentDemographics.EmigratedToUk"])
                    ),
                    new Ethnicity(
                        Number(relevantResult["CatchmentDemographics.EthArab"]),
                        Number(relevantResult["CatchmentDemographics.EthAsianOther"]),
                        Number(relevantResult["CatchmentDemographics.EthBangladeshi"]),
                        Number(relevantResult["CatchmentDemographics.EthBlackAfrican"]),
                        Number(relevantResult["CatchmentDemographics.EthBlackCaribbean"]),
                        Number(relevantResult["CatchmentDemographics.EthBlackOther"]),
                        Number(relevantResult["CatchmentDemographics.EthChinese"]),
                        Number(relevantResult["CatchmentDemographics.EthIndian"]),
                        Number(relevantResult["CatchmentDemographics.EthMixed"]),
                        Number(relevantResult["CatchmentDemographics.EthOther"]),
                        Number(relevantResult["CatchmentDemographics.EthPakistani"]),
                        Number(relevantResult["CatchmentDemographics.EthWhite"])
                    ),
                    new FirstLanguage(
                        Number(relevantResult["CatchmentDemographics.LangEnglishOrWelsh"]),
                        Number(relevantResult["CatchmentDemographics.LangOther"])
                    ),
                    new HouseholdNumberOfCars(
                        Number(relevantResult["CatchmentDemographics.Car3Plus"]),
                        Number(relevantResult["CatchmentDemographics.Car2"]),
                        Number(relevantResult["CatchmentDemographics.Car1"]),
                        Number(relevantResult["CatchmentDemographics.Car0"])
                    ),
                    new PopulationDensity(
                        Number(relevantResult["CatchmentDemographics.PopDensityLess2000"]),
                        Number(relevantResult["CatchmentDemographics.PopDensity2000to3999"]),
                        Number(relevantResult["CatchmentDemographics.PopDensity4000to5999"]),
                        Number(relevantResult["CatchmentDemographics.PopDensity6000to10000"]),
                        Number(relevantResult["CatchmentDemographics.PopDensity10000Plus"])
                    ),
                    new HouseholdIncome(
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_100kPlus"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_90to100k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_80to90k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_70to80k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_60to70k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_50to60k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_40to50k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_30to40k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_20to30k"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.CountIncome_Sub20k"])
                    ),
                    new EducationLevel(
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.NoQualification"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Level1"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Level2"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Level3"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.Level4Plus"]),
                        isNIStore ? null : Number(relevantResult["CatchmentDemographics.OtherQualification"])
                    )
                );
                catchmentDemographics.push(catchmentDemographic);
            }
        });
        return catchmentDemographics;
    } catch (error) {
        dispatch(logError("Error loading CatchmentDemographics.", error));
        throw error;
    }
};
